import * as React from 'react';
import { Sparkles, Trash2, Upload } from 'lucide-react';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMInput,
  DMLabel,
  DMText,
} from '../Primitives';

type DMImagePreviewWithActionsProps = {
  imageUrl: string;
  altText: string;
  isUploading?: boolean;
  isEnhancing?: boolean;
  shape?: 'circle' | 'rounded';
  imageSize?: string;
  showRemove?: boolean;
  fileTypeHint?: string;
  uploadLabel?: string;
  uploadingLabel?: string;
  accept?: string;
  className?: string;
  buttonWidth?: string;
  layout?: 'row' | 'column';
  onPreview?: () => void;
  onUpload: (file: File) => void;
  onRemove?: () => void;
  onEnhance?: () => void;
};

export const DMImagePreviewWithActions = ({
  imageUrl,
  altText,
  isUploading = false,
  isEnhancing = false,
  shape = 'circle',
  imageSize = 'h-[18vh] w-[18vh]',
  showRemove = true,
  fileTypeHint = 'PNG, JPEG or JPG (Max 15mb)',
  uploadLabel = 'Upload Photo',
  uploadingLabel = 'Uploading...',
  accept = 'image/png,image/jpeg,image/jpg',
  className = '',
  buttonWidth = 'w-[17vw]',
  layout = 'row',
  onPreview,
  onUpload,
  onRemove,
  onEnhance,
}: DMImagePreviewWithActionsProps) => {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onUpload(file);
    }
    event.currentTarget.value = '';
  };

  const shapeClassName = shape === 'circle' ? 'rounded-full' : 'rounded-[8px]';
  const layoutClassName =
    layout === 'row'
      ? 'flex-row items-center gap-[3.2vw]'
      : 'flex-col items-center gap-[1.5vh]';

  return (
    <DMDiv className={`flex ${layoutClassName} ${className}`}>
      <DMDiv className={`relative ${imageSize} shrink-0`}>
        <DMButton
          type="button"
          onClick={onPreview}
          disabled={isEnhancing}
          className={`h-full w-full overflow-hidden ${shapeClassName} focus:outline-none focus:ring-2 focus:ring-primary-main focus:ring-offset-2`}
          aria-label={`Preview ${altText}`}
        >
          <DMImage src={imageUrl} alt={altText} className="h-full w-full object-cover" />
        </DMButton>
        {onEnhance && (
          <DMButton
            type="button"
            onClick={onEnhance}
            disabled={isUploading || isEnhancing}
            className="absolute right-[0.8vh] top-[0.8vh] flex h-[4.2vh] w-[4.2vh] items-center justify-center rounded-full bg-white/95 text-primary-main shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Enhance image"
            title="Enhance Image"
          >
            <Sparkles className={`h-[2vh] w-[2vh] ${isEnhancing ? 'animate-pulse' : ''}`} />
          </DMButton>
        )}
        {isEnhancing && (
          <DMDiv className={`absolute inset-0 flex items-center justify-center bg-white/60 text-[1.3vh] font-semibold text-primary-main backdrop-blur-[1px] ${shapeClassName}`}>
            Enhancing...
          </DMDiv>
        )}
      </DMDiv>
      <DMDiv className="flex flex-col items-center">
        <DMLabel
          className={`flex h-[6vh] ${buttonWidth} items-center justify-center gap-[0.8vw] rounded-full border border-primary-main bg-white text-[1.85vh] font-semibold text-primary-main ${
            isUploading || isEnhancing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
          }`}
        >
          <Upload className="h-[2.1vh] w-[2.1vh]" strokeWidth={2} />
          <DMText as="span">
            {isUploading ? uploadingLabel : uploadLabel}
          </DMText>
          <DMInput
            type="file"
            accept={accept}
            className="hidden"
            disabled={isUploading || isEnhancing}
            onChange={handleFileChange}
          />
        </DMLabel>
        {showRemove && onRemove && (
          <DMButton
            type="button"
            onClick={onRemove}
            className={`mt-[1.2vh] flex h-[6vh] ${buttonWidth} items-center justify-center gap-[0.8vw] rounded-full border border-red-400 bg-white text-[1.85vh] font-semibold text-red-500`}
          >
            <Trash2 className="h-[2.1vh] w-[2.1vh]" strokeWidth={2} />
            <DMText as="span">Remove Photo</DMText>
          </DMButton>
        )}
        {fileTypeHint && (
          <DMText className="mt-[1.4vh] text-[1.6vh] font-semibold text-gray-600">
            {fileTypeHint}
          </DMText>
        )}
      </DMDiv>
    </DMDiv>
  );
};
